import { useEffect, useState } from "react";
import { ExternalLink, Globe, MapPin } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import Reveal from "./Reveal";

const TYPE_BADGE = {
  govt: { label: "GOVT", color: "bg-purple-500/15 border-purple-500/40 text-purple-300" },
  international: { label: "INTERNATIONAL", color: "bg-blue-500/15 border-blue-500/40 text-blue-300" },
  domestic: { label: "DOMESTIC", color: "bg-orange-500/15 border-orange-500/40 text-orange-300" },
};

export default function ClientsSection() {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  if (clients.length === 0) return null;

  return (
    <section className="py-24 lg:py-32 bg-[#0b0b0d]" data-testid="home-clients">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Selected Portfolio</span></Reveal>
            <Reveal delay={0.1}><h2 className="mt-4 text-4xl sm:text-5xl font-bold">Trusted by ambitious teams<br /><span className="fb-text">around the world</span></h2></Reveal>
          </div>
          <Reveal delay={0.2}><p className="text-zinc-400 max-w-md">From the Govt of West Bengal to retail brands in Australia — every engagement counts.</p></Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((c, i) => {
            const badge = TYPE_BADGE[c.project_type] || TYPE_BADGE.domestic;
            return (
              <Reveal key={c.id} delay={i * 0.05}>
                <div className="group relative rounded-3xl overflow-hidden border border-white/10 hover:border-[#FF6B1A]/40 transition-all tilt-card" data-testid={`client-card-${c.id}`}>
                  <div className="aspect-[4/3] relative overflow-hidden bg-[#1a1a1d]">
                    {c.image_url && <img src={resolveImageUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-110 transition-all duration-700" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                    <span className={`absolute top-4 left-4 text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full border ${badge.color}`}>{badge.label}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-semibold">{c.name}</h3>
                        <p className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                          {c.country && <span className="flex items-center gap-1"><MapPin size={11} /> {c.country}</span>}
                          {c.industry && <span className="flex items-center gap-1"><Globe size={11} /> {c.industry}</span>}
                        </p>
                      </div>
                      {c.website_url && (
                        <a href={c.website_url} target="_blank" rel="noreferrer" className="w-9 h-9 shrink-0 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#FF6B1A] hover:text-black hover:border-transparent transition-all" data-testid={`client-link-${c.id}`}>
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                    {c.description && <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{c.description}</p>}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
